// ---------------------------------
//  Imports
// ---------------------------------

import { gsap } from "gsap";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin();

import {
  Autoplay,
  EffectFade,
  Lazy,
  Navigation,
  Pagination,
  Swiper,
} from "swiper";

// ---------------------------------
//  Registering
// ---------------------------------

Swiper.use([Navigation, Pagination, Autoplay, Lazy, EffectFade]);
gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

// ---------------------------------
//  Global Variables
// ---------------------------------

const breakpoint = window.matchMedia("(min-width: 768px)");

// ---------------------------------
//  Scripts
// ---------------------------------

// -------------- Dividers --------------

const dividers = gsap.utils.toArray(".divider-style-1, .divider-style-2");

if (dividers.length) {
  ScrollTrigger.batch(dividers, {
    markers: false,
    start: "bottom bottom",
    onEnter: (batch) => {
      batch.forEach((el) => {
        el.classList.add("activated");
      });
    },
    onLeaveBack: (batch) => {
      batch[0].classList.remove("activated");
    },
  });
}

// -------------- Text Scramble --------------

const textToScramble = gsap.utils.toArray(".statistic");

if (textToScramble) {
  ScrollTrigger.batch(textToScramble, {
    markers: false,
    start: "bottom bottom",
    onEnter: (batch) => {
      batch.forEach((element) => {
        gsap.to(element, {
          duration: 1.2,
          scrambleText: {
            text: "{original}",
            chars: "1234567890",
            revealDelay: 0.5,
            tweenLength: false,
          },
        });
      });
    },
  });
}

// -------------- Brand Cards --------------

const debounce = (callback, wait) => {
  let timeoutId = null;
  return (...args) => {
    window.clearTimeout(timeoutId);
    timeoutId = window.setTimeout(() => {
      callback.apply(null, args);
    }, wait);
  };
};

const cards = document.querySelectorAll(".card");
const radialModifier = 0.5; // <- Best to leave this at .5
const radialMultiplier = 40;
const bgModifier = 120;

Array.from(cards).forEach((el) => {
  el.onmouseenter = (e) => {
    el.classList.add("card-active");
  };

  const debounceCard = debounce((e) => {
    let w = el.clientWidth;
    let h = el.clientHeight;
    let b = el.getBoundingClientRect();

    let X = (e.clientX - b.left) / w;
    let Y = (e.clientY - b.top) / h;

    let rX = -(X - radialModifier) * radialMultiplier;
    let rY = (Y - radialModifier) * radialMultiplier;

    let bgX = bgModifier * X;
    let bgY = bgModifier * Y;

    el.style.setProperty("--x", 100 * X + "%");
    el.style.setProperty("--y", 100 * Y + "%");

    el.style.setProperty("--bg-x", bgX + "%");
    el.style.setProperty("--bg-y", bgY + "%");

    el.style.setProperty("--r-x", rX + "deg");
    el.style.setProperty("--r-y", rY + "deg");
  }, 2);
  el.onmousemove = debounceCard;

  el.onmouseleave = (e) => {
    el.classList.remove("card-active");
  };
});

// -------------- Ticker --------------

const newsTicker = document.querySelector(".news-ticker");
const iconTicker = document.querySelector(".icon-ticker");
let newsTL = gsap.timeline();
let iconTL = gsap.timeline();
let mm = gsap.matchMedia();
let originalHTML = false;

const autoTicker = (
  tickerParent,
  kill = false,
  tickerPlay = false,
  tl = false
) => {
  if (!kill) {
    const speed = 25;

    let original_html = tickerParent.innerHTML;
    let new_html = "<div class='ticker-items'>" + original_html + "</div>";

    tickerParent.innerHTML = new_html;
    tickerParent.innerHTML += new_html;

    let tickerWidth = tickerParent.querySelector(".ticker-items").offsetWidth;
    let initDuration = tickerWidth / speed;
    let timelineTargetEL = tickerParent.querySelectorAll(".ticker-items");

    // Timeline
    tl.to(timelineTargetEL, {
      duration: initDuration,
      xPercent: -100,
      ease: "none",
      repeat: -1,
    });

    // Interactions
    if (!tickerPlay) {
      tickerParent.onmouseenter = () => tl.pause();
      tickerParent.onmouseleave = () => tl.play();
    }

    if (tickerPlay) tl.play();
  } else {
    tl.kill();
  }
};

if (newsTicker) autoTicker(newsTicker, false, false, newsTL);

if (iconTicker) {
  // add a media query. When it matches, the associated function will run
  mm.add("(max-width: 1300px)", () => {
    // this setup code only runs when viewport is at least 800px wide

    if (!originalHTML) originalHTML = iconTicker.innerHTML;
    autoTicker(iconTicker, false, true, iconTL);

    return () => {
      // custom cleanup code here (runs when it STOPS matching)
      iconTicker.innerHTML = originalHTML;
      autoTicker(iconTicker, true, false, iconTL);
    };
  });
}

// -------------- Disable Empty Links in Header Nav --------------

const topHeader = document.querySelector("#header");

if (topHeader) {
  let allHeaderLinks = topHeader.querySelectorAll("a");

  Array.from(allHeaderLinks).forEach((link) => {
    let linkHREF = link.getAttribute("href");
    if (linkHREF == "#" || linkHREF == "") {
      link.addEventListener("click", function (e) {
        e.preventDefault();
      });
    }
  });
}

// -------------- Back to top --------------

const backToTop = document.querySelector(".backtotop");
if (backToTop) {
  backToTop.onclick = (e) => {
    e.preventDefault();
    gsap.to(window, { duration: 1, scrollTo: 0 });
  };
}
